import { exactTime } from "@/lib/time"
import { cn } from "@/lib/utils"
import type { QuestionGroup, ToolCall } from "../types"

// One question and the tool calls Claude made for it, one line per call.
export default function QuestionBlock({ group }: { group: QuestionGroup }) {
  return (
    <div className="px-4 py-2.5">
      <div className="flex items-baseline gap-3 text-sm">
        <span className="w-40 shrink-0 tabular-nums">{exactTime(group.calls[0].at)}</span>
        <span className="w-36 shrink-0 truncate font-medium">{group.person}</span>
        <span className="flex-1">{group.question ?? "No question given"}</span>
      </div>
      <div className="mt-1.5 space-y-1 border-l border-border pl-3 sm:ml-40">
        {group.calls.map((call) => (
          <CallLine key={call.id} call={call} />
        ))}
      </div>
    </div>
  )
}

function CallLine({ call }: { call: ToolCall }) {
  const failed = call.error !== null || call.row_count === 0
  return (
    <div className="flex flex-wrap items-baseline gap-x-3 text-xs">
      <span className="w-36 shrink-0 font-mono">{call.tool}</span>
      <span className="min-w-48 flex-1 break-words">{inputsText(call.arguments)}</span>
      <span className={cn("w-72 shrink-0", failed && "text-destructive")}>
        {resultText(call)}
        {call.missing.map((line) => (
          <span key={line} className="block">{line}</span>
        ))}
      </span>
      <span className="w-14 shrink-0 text-right tabular-nums text-muted-foreground">{durationText(call.duration_ms)}</span>
    </div>
  )
}

function inputsText(args: Record<string, unknown>): string {
  const parts = Object.entries(args).map(([key, value]) => `${key}: ${typeof value === "string" ? value : JSON.stringify(value)}`)
  return parts.length > 0 ? parts.join(" · ") : "No inputs"
}

function resultText(call: ToolCall): string {
  if (call.error !== null) return call.error
  if (call.row_count === 0) return "No rows"
  return call.row_count === 1 ? "1 row" : `${call.row_count} rows`
}

function durationText(ms: number): string {
  return ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`
}
